import { useState, type ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { TIPOS_MOVIMIENTO_INVENTARIO } from '../../types/domain';
import { descargarCSV } from '../../lib/exportCsv';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const MES_LARGO = new Intl.DateTimeFormat('es-CO', { month: 'long', year: 'numeric' });

interface FilaRanking {
  nombre: string;
  valores: (string | number)[];
}

interface ReportesData {
  clientasNuevas: number;
  clientasFrecuentes: FilaRanking[];
  clientasInactivas: FilaRanking[];
  citasTotal: number;
  citasAtendidas: number;
  citasCanceladas: number;
  citasNoAsistio: number;
  citasPorProfesional: FilaRanking[];
  citasPorServicio: FilaRanking[];
  ventasTotal: number;
  ventasPorProfesional: FilaRanking[];
  ventasPorServicio: FilaRanking[];
  ventasPorProducto: FilaRanking[];
  valorInventario: number;
  inventarioStockBajo: FilaRanking[];
  inventarioMovimientos: FilaRanking[];
  comprasTotal: number;
  comprasPorProveedor: FilaRanking[];
  comprasPorProducto: FilaRanking[];
  gastosTotal: number;
  gastosPorCategoria: FilaRanking[];
  profesionalesResumen: FilaRanking[];
}

function periodoActual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function rangoDelPeriodo(periodo: string) {
  const [y, m] = periodo.split('-').map(Number);
  const inicio = new Date(y, m - 1, 1);
  const fin = new Date(y, m, 1);
  return { inicioISO: inicio.toISOString(), finISO: fin.toISOString(), label: MES_LARGO.format(inicio) };
}

/** Acumula (suma valor, cuenta ocurrencias) en un Map por id, conservando el nombre. */
function acumular(mapa: Map<string, { nombre: string; total: number; cantidad: number }>, id: string | null, nombre: string, valor: number) {
  if (!id) return;
  const actual = mapa.get(id) ?? { nombre, total: 0, cantidad: 0 };
  actual.total += valor;
  actual.cantidad += 1;
  mapa.set(id, actual);
}

function topPorTotal(mapa: Map<string, { nombre: string; total: number; cantidad: number }>, n: number, formatoMoneda = true): FilaRanking[] {
  return [...mapa.values()]
    .sort((a, b) => b.total - a.total)
    .slice(0, n)
    .map((f) => ({ nombre: f.nombre, valores: [f.cantidad, formatoMoneda ? COP.format(f.total) : f.total] }));
}

/**
 * Reportes (DAF v2, sección 27). Todas las cifras son consultas directas
 * a Supabase filtradas por el período elegido (mes calendario) — no hay
 * tablas de reportes pre-calculadas ni cron de agregación; para el
 * volumen de un salón individual esto es suficientemente rápido y evita
 * mantener una segunda fuente de verdad. "Clientas frecuentes/inactivas"
 * son la excepción: se calculan sobre el historial completo de citas
 * (no solo el período), con un tope de 5000 filas — más que suficiente
 * para años de operación de un solo salón.
 */
export function Reportes() {
  const [periodo, setPeriodo] = useState(periodoActual());

  const { data, loading, error } = useSupabaseQuery<ReportesData>(async () => {
    const { inicioISO, finISO } = rangoDelPeriodo(periodo);

    const [
      clientesActivosRes,
      clientesNuevasRes,
      citasHistoricoRes,
      citasPeriodoRes,
      ventasPeriodoRes,
      productosRes,
      movimientosPeriodoRes,
      comprasPeriodoRes,
      gastosPeriodoRes,
      comisionesPeriodoRes,
      pagosEmpPeriodoRes,
      profesionalesRes,
    ] = await Promise.all([
      supabase.from('clientes').select('id, nombre, apellido').eq('estado', 'activo'),
      supabase
        .from('clientes')
        .select('id', { count: 'exact', head: true })
        .gte('fecha_registro', inicioISO)
        .lt('fecha_registro', finISO),
      supabase
        .from('citas')
        .select('cliente_id, inicio, estado')
        .order('inicio', { ascending: false })
        .limit(5000),
      supabase
        .from('citas')
        .select('estado, profesionales ( nombre ), servicios ( nombre )')
        .gte('inicio', inicioISO)
        .lt('inicio', finISO)
        .limit(2000),
      supabase
        .from('ventas')
        .select(
          'total, venta_detalle ( tipo, subtotal, profesional_id, servicio_id, producto_id, profesionales ( nombre ), servicios ( nombre ), productos ( nombre ) )',
        )
        .eq('estado', 'confirmada')
        .gte('fecha', inicioISO)
        .lt('fecha', finISO)
        .limit(2000),
      supabase.from('productos').select('nombre, costo, stock_actual, stock_minimo').eq('estado', 'activo'),
      supabase
        .from('inventario_movimientos')
        .select('tipo, cantidad')
        .gte('fecha', inicioISO)
        .lt('fecha', finISO)
        .limit(3000),
      supabase
        .from('compras')
        .select('total, proveedores ( nombre ), compra_detalle ( cantidad, subtotal, productos ( nombre ) )')
        .eq('estado', 'confirmada')
        .gte('fecha', inicioISO)
        .lt('fecha', finISO)
        .limit(1000),
      supabase
        .from('gastos')
        .select('valor, categorias_gastos ( nombre )')
        .eq('estado', 'activo')
        .gte('fecha', inicioISO)
        .lt('fecha', finISO)
        .limit(2000),
      supabase.from('comisiones').select('profesional_id, monto_comision').eq('periodo', periodo).eq('estado', 'activo'),
      supabase.from('pagos_empleados').select('profesional_id, monto').eq('periodo', periodo).eq('estado', 'pagado'),
      supabase.from('profesionales').select('id, nombre').eq('estado', 'activo'),
    ]);

    const resultados = [
      clientesActivosRes,
      clientesNuevasRes,
      citasHistoricoRes,
      citasPeriodoRes,
      ventasPeriodoRes,
      productosRes,
      movimientosPeriodoRes,
      comprasPeriodoRes,
      gastosPeriodoRes,
      comisionesPeriodoRes,
      pagosEmpPeriodoRes,
      profesionalesRes,
    ];
    const primerError = resultados.find((r) => r.error)?.error;
    if (primerError) return { data: null, error: primerError };

    // ---- Clientas: frecuentes (histórico) e inactivas ----
    const citasHistorico = (citasHistoricoRes.data ?? []) as Array<{
      cliente_id: string;
      inicio: string;
      estado: string;
    }>;
    const clientesActivos = (clientesActivosRes.data ?? []) as Array<{ id: string; nombre: string; apellido: string | null }>;

    const frecuenciaPorCliente = new Map<string, number>();
    const ultimaAtendidaPorCliente = new Map<string, string>();
    for (const c of citasHistorico) {
      if (c.estado === 'atendida') {
        frecuenciaPorCliente.set(c.cliente_id, (frecuenciaPorCliente.get(c.cliente_id) ?? 0) + 1);
        if (!ultimaAtendidaPorCliente.has(c.cliente_id)) ultimaAtendidaPorCliente.set(c.cliente_id, c.inicio);
      }
    }
    const nombrePorCliente = new Map(clientesActivos.map((c) => [c.id, `${c.nombre} ${c.apellido ?? ''}`.trim()]));

    const clientasFrecuentes: FilaRanking[] = [...frecuenciaPorCliente.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([clienteId, veces]) => ({ nombre: nombrePorCliente.get(clienteId) ?? 'Clienta', valores: [veces] }));

    const haceSesentaDias = new Date();
    haceSesentaDias.setDate(haceSesentaDias.getDate() - 60);
    const clientasInactivas: FilaRanking[] = clientesActivos
      .map((c) => ({ nombre: `${c.nombre} ${c.apellido ?? ''}`.trim(), ultima: ultimaAtendidaPorCliente.get(c.id) ?? null }))
      .filter((c) => !c.ultima || new Date(c.ultima) < haceSesentaDias)
      .slice(0, 15)
      .map((c) => ({ nombre: c.nombre, valores: [c.ultima ? new Date(c.ultima).toLocaleDateString('es-CO') : 'Sin citas atendidas'] }));

    // ---- Citas del período ----
    const citasPeriodo = (citasPeriodoRes.data ?? []) as unknown as Array<{
      estado: string;
      profesionales: { nombre: string } | null;
      servicios: { nombre: string } | null;
    }>;
    const porProfesionalCitas = new Map<string, { nombre: string; total: number; cantidad: number }>();
    const porServicioCitas = new Map<string, { nombre: string; total: number; cantidad: number }>();
    let citasAtendidas = 0;
    let citasCanceladas = 0;
    let citasNoAsistio = 0;
    for (const c of citasPeriodo) {
      if (c.estado === 'atendida') citasAtendidas += 1;
      if (c.estado === 'cancelada') citasCanceladas += 1;
      if (c.estado === 'no_asistio') citasNoAsistio += 1;
      const nombreProf = c.profesionales?.nombre ?? 'Sin asignar';
      acumular(porProfesionalCitas, nombreProf, nombreProf, 0);
      const nombreServ = c.servicios?.nombre ?? 'Sin servicio';
      acumular(porServicioCitas, nombreServ, nombreServ, 0);
    }

    // ---- Ventas del período (desglose por profesional/servicio/producto) ----
    const ventasPeriodo = (ventasPeriodoRes.data ?? []) as unknown as Array<{
      total: number;
      venta_detalle: Array<{
        tipo: 'servicio' | 'producto';
        subtotal: number;
        profesional_id: string | null;
        servicio_id: string | null;
        producto_id: string | null;
        profesionales: { nombre: string } | null;
        servicios: { nombre: string } | null;
        productos: { nombre: string } | null;
      }>;
    }>;
    const ventasTotal = ventasPeriodo.reduce((s, v) => s + v.total, 0);
    const porProfesionalVentas = new Map<string, { nombre: string; total: number; cantidad: number }>();
    const porServicioVentas = new Map<string, { nombre: string; total: number; cantidad: number }>();
    const porProductoVentas = new Map<string, { nombre: string; total: number; cantidad: number }>();
    for (const v of ventasPeriodo) {
      for (const d of v.venta_detalle ?? []) {
        if (d.profesional_id) acumular(porProfesionalVentas, d.profesional_id, d.profesionales?.nombre ?? 'Sin asignar', d.subtotal);
        if (d.tipo === 'servicio' && d.servicio_id) acumular(porServicioVentas, d.servicio_id, d.servicios?.nombre ?? 'Servicio', d.subtotal);
        if (d.tipo === 'producto' && d.producto_id) acumular(porProductoVentas, d.producto_id, d.productos?.nombre ?? 'Producto', d.subtotal);
      }
    }

    // ---- Inventario (foto actual + movimientos del período) ----
    const productos = (productosRes.data ?? []) as Array<{ nombre: string; costo: number; stock_actual: number; stock_minimo: number }>;
    const valorInventario = productos.reduce((s, p) => s + p.costo * p.stock_actual, 0);
    const inventarioStockBajo: FilaRanking[] = productos
      .filter((p) => p.stock_actual <= p.stock_minimo)
      .sort((a, b) => a.stock_actual - b.stock_actual)
      .slice(0, 10)
      .map((p) => ({ nombre: p.nombre, valores: [p.stock_actual, p.stock_minimo] }));

    const movimientos = (movimientosPeriodoRes.data ?? []) as Array<{ tipo: string; cantidad: number }>;
    const movPorTipo = new Map<string, number>();
    for (const m of movimientos) movPorTipo.set(m.tipo, (movPorTipo.get(m.tipo) ?? 0) + 1);
    const inventarioMovimientos: FilaRanking[] = TIPOS_MOVIMIENTO_INVENTARIO.filter((t) => movPorTipo.has(t.value)).map((t) => ({
      nombre: t.label,
      valores: [movPorTipo.get(t.value) ?? 0],
    }));

    // ---- Compras del período ----
    const comprasPeriodo = (comprasPeriodoRes.data ?? []) as unknown as Array<{
      total: number;
      proveedores: { nombre: string } | null;
      compra_detalle: Array<{ cantidad: number; subtotal: number; productos: { nombre: string } | null }>;
    }>;
    const comprasTotal = comprasPeriodo.reduce((s, c) => s + c.total, 0);
    const porProveedorCompras = new Map<string, { nombre: string; total: number; cantidad: number }>();
    const porProductoCompras = new Map<string, { nombre: string; total: number; cantidad: number }>();
    for (const c of comprasPeriodo) {
      const nombreProv = c.proveedores?.nombre ?? 'Proveedor';
      acumular(porProveedorCompras, nombreProv, nombreProv, c.total);
      for (const d of c.compra_detalle ?? []) {
        const nombreProd = d.productos?.nombre ?? 'Producto';
        acumular(porProductoCompras, nombreProd, nombreProd, d.subtotal);
      }
    }

    // ---- Gastos del período ----
    const gastosPeriodo = (gastosPeriodoRes.data ?? []) as unknown as Array<{ valor: number; categorias_gastos: { nombre: string } | null }>;
    const gastosTotal = gastosPeriodo.reduce((s, g) => s + g.valor, 0);
    const porCategoriaGastos = new Map<string, { nombre: string; total: number; cantidad: number }>();
    for (const g of gastosPeriodo) {
      const nombreCat = g.categorias_gastos?.nombre ?? 'Otros';
      acumular(porCategoriaGastos, nombreCat, nombreCat, g.valor);
    }

    // ---- Resumen por profesional (servicios, ventas, comisiones, pagos) ----
    const profesionales = (profesionalesRes.data ?? []) as Array<{ id: string; nombre: string }>;
    const comisionesPorProf = new Map<string, number>();
    for (const c of (comisionesPeriodoRes.data ?? []) as Array<{ profesional_id: string; monto_comision: number }>) {
      comisionesPorProf.set(c.profesional_id, (comisionesPorProf.get(c.profesional_id) ?? 0) + c.monto_comision);
    }
    const pagosPorProf = new Map<string, number>();
    for (const p of (pagosEmpMesPeriodoSeguro(pagosEmpPeriodoRes.data)) as Array<{ profesional_id: string; monto: number }>) {
      pagosPorProf.set(p.profesional_id, (pagosPorProf.get(p.profesional_id) ?? 0) + p.monto);
    }
    const profesionalesResumen: FilaRanking[] = profesionales.map((p) => {
      const ventasProf = porProfesionalVentas.get(p.id);
      return {
        nombre: p.nombre,
        valores: [
          ventasProf?.cantidad ?? 0,
          COP.format(ventasProf?.total ?? 0),
          COP.format(comisionesPorProf.get(p.id) ?? 0),
          COP.format(pagosPorProf.get(p.id) ?? 0),
        ],
      };
    });

    return {
      data: {
        clientasNuevas: clientesNuevasRes.count ?? 0,
        clientasFrecuentes,
        clientasInactivas,
        citasTotal: citasPeriodo.length,
        citasAtendidas,
        citasCanceladas,
        citasNoAsistio,
        citasPorProfesional: [...porProfesionalCitas.values()].sort((a, b) => b.cantidad - a.cantidad).map((f) => ({ nombre: f.nombre, valores: [f.cantidad] })),
        citasPorServicio: [...porServicioCitas.values()].sort((a, b) => b.cantidad - a.cantidad).map((f) => ({ nombre: f.nombre, valores: [f.cantidad] })),
        ventasTotal,
        ventasPorProfesional: topPorTotal(porProfesionalVentas, 8),
        ventasPorServicio: topPorTotal(porServicioVentas, 8),
        ventasPorProducto: topPorTotal(porProductoVentas, 8),
        valorInventario,
        inventarioStockBajo,
        inventarioMovimientos,
        comprasTotal,
        comprasPorProveedor: topPorTotal(porProveedorCompras, 8),
        comprasPorProducto: topPorTotal(porProductoCompras, 8),
        gastosTotal,
        gastosPorCategoria: topPorTotal(porCategoriaGastos, 8),
        profesionalesResumen,
      },
      error: null,
    };
  }, [periodo]);

  const { label: periodoLabel } = rangoDelPeriodo(periodo);

  return (
    <div>
      <PageHeader
        title="Reportes"
        description="Indicadores operativos y financieros — DAF v2, sección 27"
        actions={
          <input
            type="month"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="rounded-md border border-onyx/15 px-3 py-2 text-sm"
          />
        }
      />

      {loading && <p className="text-sm text-onyx/50">Cargando reportes…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && (
        <div className="space-y-8">
          <p className="text-sm capitalize text-onyx/50">Período: {periodoLabel}</p>

          <Seccion titulo="Clientas">
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <MetricCard label="Clientas nuevas en el período" value={data.clientasNuevas} />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <TablaRanking titulo="Más frecuentes (histórico)" columnas={['Clienta', 'Citas atendidas']} filas={data.clientasFrecuentes} vacio="Aún no hay citas atendidas." periodo={periodo} />
              <TablaRanking titulo="Inactivas (60+ días sin visitar)" columnas={['Clienta', 'Última visita']} filas={data.clientasInactivas} vacio="Todas las clientas activas están al día." periodo={periodo} />
            </div>
          </Seccion>

          <Seccion titulo="Citas del período">
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard label="Total de citas" value={data.citasTotal} />
              <MetricCard label="Atendidas" value={data.citasAtendidas} />
              <MetricCard label="Canceladas" value={data.citasCanceladas} alerta={data.citasCanceladas > 0} />
              <MetricCard label="No asistió" value={data.citasNoAsistio} alerta={data.citasNoAsistio > 0} />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <TablaRanking titulo="Por profesional" columnas={['Profesional', 'Citas']} filas={data.citasPorProfesional} vacio="Sin citas en el período." periodo={periodo} />
              <TablaRanking titulo="Por servicio" columnas={['Servicio', 'Citas']} filas={data.citasPorServicio} vacio="Sin citas en el período." periodo={periodo} />
            </div>
          </Seccion>

          <Seccion titulo="Ventas del período">
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <MetricCard label="Ventas del período" value={COP.format(data.ventasTotal)} />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <TablaRanking titulo="Por profesional" columnas={['Profesional', 'Líneas', 'Total']} filas={data.ventasPorProfesional} vacio="Sin ventas atribuidas a profesionales." periodo={periodo} />
              <TablaRanking titulo="Servicios más vendidos" columnas={['Servicio', 'Veces', 'Total']} filas={data.ventasPorServicio} vacio="Sin servicios vendidos." periodo={periodo} />
              <TablaRanking titulo="Productos más vendidos" columnas={['Producto', 'Veces', 'Total']} filas={data.ventasPorProducto} vacio="Sin productos vendidos." periodo={periodo} />
            </div>
          </Seccion>

          <Seccion titulo="Inventario">
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <MetricCard label="Valor del inventario (a costo)" value={COP.format(data.valorInventario)} />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <TablaRanking titulo="Stock bajo o agotado" columnas={['Producto', 'Stock actual', 'Stock mínimo']} filas={data.inventarioStockBajo} vacio="Ningún producto por debajo de su stock mínimo." periodo={periodo} />
              <TablaRanking titulo="Movimientos del período por tipo" columnas={['Tipo', 'Movimientos']} filas={data.inventarioMovimientos} vacio="Sin movimientos en el período." periodo={periodo} />
            </div>
          </Seccion>

          <Seccion titulo="Compras del período">
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <MetricCard label="Compras del período" value={COP.format(data.comprasTotal)} />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <TablaRanking titulo="Por proveedor" columnas={['Proveedor', 'Compras', 'Total']} filas={data.comprasPorProveedor} vacio="Sin compras en el período." periodo={periodo} />
              <TablaRanking titulo="Por producto" columnas={['Producto', 'Líneas', 'Total']} filas={data.comprasPorProducto} vacio="Sin compras en el período." periodo={periodo} />
            </div>
          </Seccion>

          <Seccion titulo="Gastos del período">
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <MetricCard label="Gastos del período" value={COP.format(data.gastosTotal)} />
            </div>
            <TablaRanking titulo="Por categoría" columnas={['Categoría', 'Registros', 'Total']} filas={data.gastosPorCategoria} vacio="Sin gastos en el período." periodo={periodo} />
          </Seccion>

          <Seccion titulo="Profesionales">
            <TablaRanking
              titulo="Servicios, ventas, comisiones y pagos del período"
              columnas={['Profesional', 'Servicios realizados', 'Ventas generadas', 'Comisiones', 'Pagos realizados']}
              filas={data.profesionalesResumen}
              vacio="No hay profesionales activos."
              periodo={periodo}
            />
          </Seccion>
        </div>
      )}
    </div>
  );
}

/** pagos_empleados sin período coincidente puede venir null si la consulta falló silenciosamente por RLS; nunca debería ocurrir aquí porque ya se valida error arriba, pero se protege igual. */
function pagosEmpMesPeriodoSeguro<T>(valor: T[] | null): T[] {
  return valor ?? [];
}

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-onyx/40">{titulo}</h3>
      {children}
    </div>
  );
}

function MetricCard({ label, value, alerta }: { label: string; value: number | string; alerta?: boolean }) {
  return (
    <div className={`card ${alerta ? 'border-amber-300 bg-amber-50' : ''}`}>
      <p className="text-sm text-onyx/50">{label}</p>
      <p className={`mt-2 font-display text-2xl ${alerta ? 'text-amber-700' : 'text-gold-deep'}`}>{value}</p>
    </div>
  );
}

function TablaRanking({
  titulo,
  columnas,
  filas,
  vacio,
  periodo,
}: {
  titulo: string;
  columnas: string[];
  filas: FilaRanking[];
  vacio: string;
  /** Mes en formato "YYYY-MM" del reporte actual, solo para nombrar el archivo al descargar en CSV. */
  periodo?: string;
}) {
  function descargar() {
    const nombreArchivo = periodo ? `${titulo} - ${periodo}` : titulo;
    descargarCSV(
      nombreArchivo,
      columnas,
      filas.map((f) => [f.nombre, ...f.valores]),
    );
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-medium text-onyx/70">{titulo}</h4>
        {filas.length > 0 && (
          <button type="button" className="text-xs font-medium text-gold-deep hover:underline" onClick={descargar}>
            Descargar CSV
          </button>
        )}
      </div>
      {filas.length === 0 ? (
        <p className="text-sm text-onyx/40">{vacio}</p>
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                {columnas.map((c) => (
                  <th key={c} className="px-4 py-2 font-medium">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, i) => (
                <tr key={i} className="border-b border-onyx/5 last:border-0">
                  <td className="px-4 py-2">{fila.nombre}</td>
                  {fila.valores.map((v, j) => (
                    <td key={j} className="px-4 py-2">
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
